import { CheckCircleIcon as CheckCircleIconSolid } from "@heroicons/react/16/solid";
import { CheckCircleIcon as CheckCircleIconOutline } from "@heroicons/react/24/outline";
import passwordSuccess from "asset/img/icons/check_complete.svg";
import passwordFailure from "asset/img/icons/check_incomplete.svg";
import InputError from "component/InputError";
import Label from "component/Label";
import Text from "component/Text";
import { IBaseInputCommonProps } from "component/interface/Input";
import { Divider } from "primereact/divider";
import { InputTextProps } from "primereact/inputtext";
import { Password } from "primereact/password";
import React, { useState } from "react";
import { Trans } from "react-i18next";
import {
  verifyCharacterLimit,
  verifyLowerCase,
  verifyNumber,
  verifySpecialCharacter,
  verifyUpperCase,
} from "util/CommonUtil";

export interface IBaseInputPasswordProps extends InputTextProps, IBaseInputCommonProps {
  feedback?: boolean;
  toggleMask?: boolean;
  type?: string;
  onChangeTextState?: any;
}

const BasePassword: React.FC<IBaseInputPasswordProps> = (props: any) => {
  const {
    hideLabel,
    hideErrorRow,
    label,
    validation,
    hideError,
    labelClassNames,
    labelStyleObj,
    errorClassNames,
    info,
    ...basePrimeReactProps
  } = props;

  const {
    className,
    value,
    style,
    onChange,
    name,
    placeholder,
    onChangeTextState,
    ...primeReactProps
  } = basePrimeReactProps;

  const [checkPasswordConditions, setCheckPasswordConditions] = useState({
    verifyMinChar: false,
    verifyNumeric: false,
    verifyLowCase: false,
    verifyHasUpperCase: false,
    verifySpecialChar: false,
  });

  const header = () => {
    return (
      <Text
        className="text-light-grey-900 dark:text-dark-neutral-gray-900"
        label="label.text.password.header"
      />
    );
  };

  const footer = () => {
    const { verifyMinChar, verifyNumeric, verifyLowCase, verifyHasUpperCase, verifySpecialChar } =
      checkPasswordConditions;

    return (
      <React.Fragment>
        <Divider className="mt-4 border border-light-grey-500 dark:border-dark-neutral-gray-400/70" />

        <div className="mt-4 text-body-copy-1">
          <Text
            className="font-semibold text-light-grey-900 dark:text-dark-neutral-gray-800"
            label="label.text.password.suggestions"
          />

          <div className="mt-3 leading-7 text-light-grey-800 dark:text-primary-gray-700">
            <div className="flex flex-col gap-2">
              {/* <div className="float-right"> */}
              <div className="flex items-center gap-3">
                {/* <img
                  src={verifyMinChar ? passwordSuccess : passwordFailure}
                  alt="Password not success icon"
                /> */}
                {verifyMinChar ? (
                  <CheckCircleIconSolid className="h-5 w-5 text-light-brand-blue-700 dark:text-primary-electron-blue-700" />
                ) : (
                  <CheckCircleIconOutline className="h-5 w-5 text-light-grey-400 dark:text-dark-neutral-gray-700" />
                )}
                <Trans>{"label.text.password.characters"}</Trans>
              </div>

              <div className="flex items-center gap-3">
                {/* <img
                  src={verifyLowCase ? passwordSuccess : passwordFailure}
                  alt="Password not success icon"
                  className="float-right"
                /> */}
                {verifyLowCase ? (
                  <CheckCircleIconSolid className="h-5 w-5 text-light-brand-blue-700 dark:text-primary-electron-blue-700" />
                ) : (
                  <CheckCircleIconOutline className="h-5 w-5 text-light-grey-400 dark:text-dark-neutral-gray-700" />
                )}
                <Trans>{"label.text.password.smallLetter"}</Trans>
              </div>

              <div className="flex items-center gap-3">
                {/* <img
                  src={verifyHasUpperCase ? passwordSuccess : passwordFailure}
                  alt="Password not success icon"
                  className="float-right"
                /> */}
                {verifyHasUpperCase ? (
                  <CheckCircleIconSolid className="h-5 w-5 text-light-brand-blue-700 dark:text-primary-electron-blue-700" />
                ) : (
                  <CheckCircleIconOutline className="h-5 w-5 text-light-grey-400 dark:text-dark-neutral-gray-700" />
                )}
                <Trans>{"label.text.password.capitalLetter"}</Trans>
              </div>

              <div className="flex items-center gap-3">
                {/* <img
                  src={verifyNumeric ? passwordSuccess : passwordFailure}
                  alt="Password not success icon"
                  className="float-right"
                /> */}
                {verifyNumeric ? (
                  <CheckCircleIconSolid className="h-5 w-5 text-light-brand-blue-700 dark:text-primary-electron-blue-700" />
                ) : (
                  <CheckCircleIconOutline className="h-5 w-5 text-light-grey-400 dark:text-dark-neutral-gray-700" />
                )}
                <Trans>{"label.text.password.number"}</Trans>
              </div>

              <div className="flex items-center gap-3">
                {/* <img
                  src={verifySpecialChar ? passwordSuccess : passwordFailure}
                  alt="Password not success icon"
                  className="float-right"
                /> */}
                {verifySpecialChar ? (
                  <CheckCircleIconSolid className="h-5 w-5 text-light-brand-blue-700 dark:text-primary-electron-blue-700" />
                ) : (
                  <CheckCircleIconOutline className="h-5 w-5 text-light-grey-400 dark:text-dark-neutral-gray-700" />
                )}
                <Trans>{"label.text.password.specialChar"}</Trans>
              </div>
            </div>
          </div>
        </div>
      </React.Fragment>
    );
  };

  const updatePasswordCondition = (text: string, state: boolean) => {
    setCheckPasswordConditions((prevState) => {
      return { ...prevState, [text]: state };
    });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    //Verifying the character limit
    if (verifyCharacterLimit(e.target.value, 8)) {
      updatePasswordCondition("verifyMinChar", true);
    } else {
      updatePasswordCondition("verifyMinChar", false);
    }

    // Verify presence of number
    if (verifyNumber(e.target.value)) {
      updatePasswordCondition("verifyNumeric", true);
    } else {
      updatePasswordCondition("verifyNumeric", false);
    }

    // Verify presence of lower case

    if (verifyLowerCase(e.target.value)) {
      updatePasswordCondition("verifyLowCase", true);
    } else {
      updatePasswordCondition("verifyLowCase", false);
    }

    // Verify presence of upper case
    if (verifyUpperCase(e.target.value)) {
      updatePasswordCondition("verifyHasUpperCase", true);
    } else {
      updatePasswordCondition("verifyHasUpperCase", false);
    }

    // Verify presence of special character
    if (verifySpecialCharacter(e.target.value)) {
      updatePasswordCondition("verifySpecialChar", true);
    } else {
      updatePasswordCondition("verifySpecialChar", false);
    }

    onChange(e);
  };

  return (
    <div className="input-password-field relative w-full">
      {!hideLabel && (
        <div className="">
          <Label
            label={label}
            info={info}
            labelClassNames={labelClassNames}
            labelStyleObj={labelStyleObj}
            isInvalid={validation}
          />
        </div>
      )}

      <div className={`relative w-full`}>
        <Password
          id="password-container"
          className={`w-full`}
          toggleMask
          inputClassName={`pr-8 ${className}`}
          value={value}
          style={style}
          onChange={handleChange}
          name={name}
          placeholder={placeholder}
          header={header}
          footer={footer}
          // onChangeTextState={onChangeTextState} //used to get the label for the password from library but facing some errors
          {...primeReactProps}
        />
      </div>

      {!hideError && (
        <InputError
          validation={validation}
          hideErrorRow={hideErrorRow}
          errorClassNames={errorClassNames + " mt-1"}
        />
      )}
    </div>
  );
};

export default BasePassword;
